import React from "react";
import * as momentTZ from "moment-timezone";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/index.ts";
import { readBranches } from "../../../services/branch.service.ts";
import {
  findIncidents,
  findIncidentsBySchedules,
} from "../../../services/incident.service.ts";

interface Branch {
  id: number;
  name: string;
}

interface IncidentsStats {
  total: number;
  byStatus: Record<string, number>;
}

const IncidentsStatsCard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [branches, setBranches] = React.useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = React.useState<number | null>(
    user?.branchId ?? null
  );
  const [stats, setStats] = React.useState<IncidentsStats>({
    total: 0,
    byStatus: {},
  });
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchBranches = React.useCallback(async () => {
    try {
      const response = await readBranches();
      const list: Branch[] = response || [];
      setBranches(list);

      if (!selectedBranchId) {
        const fromUser = user?.branchId;
        const fallback = list[0]?.id;
        setSelectedBranchId(fromUser ?? fallback ?? null);
      }
    } catch (e) {
      console.error("Error fetching branches for incidents stats:", e);
    }
  }, [selectedBranchId, user?.branchId]);

  const fetchStats = React.useCallback(async () => {
    if (!selectedBranchId) {
      setStats({ total: 0, byStatus: {} });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const recordDate = momentTZ.tz("America/Mexico_City").format("YYYY-MM-DD");

      const data = await findIncidents(selectedBranchId, recordDate);
      const rawIncidents = (data?.incidencias || []) as any[];
      const rawUnscheduled = (data?.noProgramados || []) as any[];

      // Misma limpieza base de la tabla de incidencias.
      const filteredPending = rawIncidents.filter((incident) => {
        const scheduleStatus = incident?.schedule?.assistanceStatus;
        const incidentStatus = incident?.status;
        if (
          scheduleStatus === "Día libre gozado" &&
          incidentStatus === "Día libre gozado"
        ) {
          return false;
        }
        if (!incident?.schedule?.id || !incidentStatus) return false;
        return true;
      });

      const seenPendingKeys = new Set<string>();
      const dedupedPending = filteredPending.filter((incident) => {
        const key = `${incident.schedule?.id}-${incident.status}`;
        if (seenPendingKeys.has(key)) return false;
        seenPendingKeys.add(key);
        return true;
      });

      const scheduleIds = ((data?.programados || []) as any[])
        .map((p: any) => p?.id)
        .filter((id: any): id is number => typeof id === "number");

      let resolved: any[] = [];
      if (scheduleIds.length > 0) {
        const savedIncidents = await findIncidentsBySchedules(scheduleIds);
        resolved = (savedIncidents || []).filter(
          (inc: any) => inc?.finalStatus === true && inc?.schedule?.id && inc?.status
        );
      }

      const resolvedKeys = new Set(
        resolved.map((inc: any) => `${inc.schedule?.id}-${inc.status}`)
      );
      const uniquePending = dedupedPending.filter(
        (inc) => !resolvedKeys.has(`${inc.schedule?.id}-${inc.status}`)
      );

      const seenUnscheduledKeys = new Set<string>();
      const dedupedUnscheduled = rawUnscheduled.filter((item: any) => {
        const partnerId = item?.partner?.id;
        const checkInId = item?.checkIn?.id;
        if (!partnerId || !checkInId) return false;
        const key = `${partnerId}-${checkInId}`;
        if (seenUnscheduledKeys.has(key)) return false;
        seenUnscheduledKeys.add(key);
        return true;
      });

      const allIncidentsTotal =
        uniquePending.length + resolved.length + dedupedUnscheduled.length;

      const byStatus: Record<string, number> = {};
      [...uniquePending, ...resolved].forEach((inc) => {
        const status = (inc.status || inc.schedule?.assistanceStatus || "Incidencia")
          .toString()
          .trim();
        if (!status) return;
        byStatus[status] = (byStatus[status] || 0) + 1;
      });
      if (dedupedUnscheduled.length > 0) {
        byStatus["No programado"] =
          (byStatus["No programado"] || 0) + dedupedUnscheduled.length;
      }

      setStats({ total: allIncidentsTotal, byStatus });
    } catch (e) {
      console.error("Error fetching incidents stats:", e);
      setError("No se pudieron cargar las incidencias.");
      setStats({ total: 0, byStatus: {} });
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId]);

  React.useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  React.useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const renderTooltipContent = () => {
    const entries = Object.entries(stats.byStatus);
    if (!entries.length) return "Sin incidencias registradas.";

    return entries
      .map(([status, count]) => `${status}: ${count}`)
      .join(" • ");
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h3 style={styles.title}>Incidencias</h3>
          <p style={styles.subtitle}>Total del día en la sucursal.</p>
        </div>
        <div style={styles.headerRight}>
          <select
            style={styles.branchSelect}
            value={selectedBranchId ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedBranchId(value ? Number(value) : null);
            }}
          >
            <option value="" disabled>
              Sucursal
            </option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={styles.body}>
        {loading ? (
          <div style={styles.loadingBody}>
            <i
              className="fa-solid fa-spinner fa-spin"
              style={{ fontSize: 18, color: "#6b7280" }}
            ></i>
          </div>
        ) : error ? (
          <div style={styles.errorBody}>{error}</div>
        ) : (
          <div style={styles.circleWrapper}>
            <button
              type="button"
              style={styles.circleButton}
              title={renderTooltipContent()}
              disabled={!selectedBranchId}
              onClick={() => {
                if (!selectedBranchId) return;
                window.open(
                  `/sucursales/incidencias/${selectedBranchId}`,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              <span style={styles.circleValue}>{stats.total}</span>
            </button>
            <div style={styles.circleLabel}>Incidencias</div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.06)",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    height: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
  },
  title: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
  },
  subtitle: {
    margin: 0,
    fontSize: 11,
    color: "#6b7280",
  },
  branchSelect: {
    minWidth: 140,
    padding: "4px 8px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    backgroundColor: "#f9fafb",
    fontSize: 11,
    color: "#111827",
    fontWeight: 600,
    outline: "none",
  },
  body: {
    marginTop: 4,
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingBody: {
    padding: "16px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  errorBody: {
    padding: "12px 0",
    fontSize: 12,
    color: "#b91c1c",
  },
  circleWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 0 6px 0",
    gap: 4,
  },
  circleButton: {
    width: 160,
    height: 160,
    borderRadius: "50%",
    border: "2px solid rgba(234, 88, 12, 0.9)",
    backgroundColor: "#f97316",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 24px rgba(234,88,12,0.45)",
    cursor: "pointer",
  },
  circleValue: {
    fontSize: 48,
    fontWeight: 700,
    color: "#ffffff",
  },
  circleLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
};

export default IncidentsStatsCard;


